// ─────────────────────────────────────────────
// Oike · Mi recorrido — catalog labels and the PROPOSED recommendation rules.
//
// The person's space shows their processes, progress, approved results and a
// recommended route of courses (shared/journey.ts builds it on the server,
// src/journey.js has the pure rules). This file has:
//
// - COURSE_TOPICS, COURSE_LEVELS, COURSE_ACCESS: the labels a course can carry.
//   They describe the content of a course, never the person.
// - PROPOSED_RULES: the starting point of the recommendation rules. The rules in
//   force are managed by Ale in Oike (Aprendizaje → Reglas de recomendación) and
//   stored in Coaching Settings (Key "journey_rules") with a version and the
//   history of every change (shared/journey.ts › loadRules / saveRules). Until she
//   saves them, these proposals apply, all inactive: everybody sees the basic route.
// - APTITUDE_NAMES: the words that name an aptitude in the titles of an approved
//   profile. They only SUGGEST focus areas to Ale for older profiles; a
//   recommendation never comes from titles alone (docs/coaching/mi-recorrido.md).
// Values stored in Airtable are the English keys; Spanish labels live here.
// ─────────────────────────────────────────────

/** Topics or skills a course works on (Coaching Courses › Topics, a JSON list of keys). */
export const COURSE_TOPICS = [
  { key: 'communication', label: 'Comunicación' },
  { key: 'listening', label: 'Escucha' },
  { key: 'planning', label: 'Planificación' },
  { key: 'follow_up', label: 'Seguimiento' },
  { key: 'negotiation', label: 'Negociación' },
  { key: 'leadership', label: 'Liderazgo' },
  { key: 'prospecting', label: 'Prospección' },
  { key: 'discovery', label: 'Descubrimiento de necesidades' },
  { key: 'qualification', label: 'Calificación de oportunidades' },
  { key: 'proposal', label: 'Propuestas' },
  { key: 'tools', label: 'Herramientas e IA' },
  { key: 'self_management', label: 'Gestión personal' },
  // Marks a course as specific to sales: never offered automatically to someone who did not choose that specialty
  { key: 'sales', label: 'Ventas (curso específico)' },
];

export const COURSE_LEVELS = [
  { key: 'intro', label: 'Inicial' },
  { key: 'intermediate', label: 'Intermedio' },
  { key: 'advanced', label: 'Avanzado' },
];

/**
 * Who can start a course. "assigned" (the default): only when the coach assigns
 * it; it is never offered to the person, also when a rule matches (Ale sees it
 * in Personas to assign it). "open": included for people with a personal space:
 * the person can start it from "Tu ruta recomendada". A course that needs a
 * purchase is never "open".
 */
export const COURSE_ACCESS = [
  { key: 'assigned', label: 'Solo por asignación', hint: 'La persona lo ve cuando se lo asignas. Si una regla lo recomienda, aparece solo en su ficha para que decidas.' },
  { key: 'open', label: 'Incluido: puede empezarlo por su cuenta', hint: 'Si se lo recomienda una regla o la ruta básica, la persona puede empezarlo desde su espacio. Nunca marques así un curso pago.' },
];

/** At most this many personalized recommendations at a time: a manageable route, not a catalog. */
export const MAX_RECOMMENDATIONS = 3;

/** Names of each aptitude (src/diagnosticConfig.js › aptitudes) as an approved profile writes them, for suggestions only. Lowercase, without accents. */
export const APTITUDE_NAMES = {
  preparation: ['preparacion y priorizacion', 'preparacion', 'priorizacion'],
  prospecting: ['prospeccion'],
  discovery: ['discovery', 'descubrimiento'],
  qualification: ['calificacion'],
  proposal: ['propuesta'],
  negotiation: ['negociacion y cierre', 'negociacion'],
  followUp: ['seguimiento y pipeline', 'seguimiento'],
  toolsAI: ['herramientas e ia', 'herramientas'],
};

/**
 * One rule: when the reference result names `aptitude` in "En qué enfocarte"
 * (source "focus") or in "Lo que haces muy bien" (source "strength"), recommend
 * published courses marked "Puede recomendarse" that share a topic with
 * `topics` and whose level is in `courseLevels`. Prerequisites and access are
 * always checked. `reason` is what the person reads ({aptitude} becomes the
 * aptitude's name, as their profile says it).
 *
 * PROPOSALS (active: false): the topics and levels are Ale's decision. She
 * edits, activates and deactivates them in Oike; this list is only the start.
 */
export const PROPOSED_RULES = [
  { id: 'focus-preparation', active: false, source: 'focus', aptitude: 'preparation', topics: ['planning'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
  { id: 'focus-prospecting', active: false, source: 'focus', aptitude: 'prospecting', topics: ['prospecting', 'communication'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
  { id: 'focus-discovery', active: false, source: 'focus', aptitude: 'discovery', topics: ['discovery', 'listening'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
  { id: 'focus-qualification', active: false, source: 'focus', aptitude: 'qualification', topics: ['qualification'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
  { id: 'focus-proposal', active: false, source: 'focus', aptitude: 'proposal', topics: ['proposal', 'communication'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
  { id: 'focus-negotiation', active: false, source: 'focus', aptitude: 'negotiation', topics: ['negotiation'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
  { id: 'focus-followUp', active: false, source: 'focus', aptitude: 'followUp', topics: ['follow_up', 'planning'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
  { id: 'focus-toolsAI', active: false, source: 'focus', aptitude: 'toolsAI', topics: ['tools'], courseLevels: ['intro', 'intermediate'], reason: 'Tu ficha del diagnóstico marca «{aptitude}» como un área para enfocarte.' },
];

/** Limits of the rules Ale manages in Oike. */
// history: versions kept; snapshots: the most recent versions that keep their full rules (the older ones keep
// what changed); maxChars: the stored value stays under Airtable's long text limit (100 000 characters)
export const RULE_LIMITS = { rules: 40, topics: 6, reason: 300, note: 300, history: 50, snapshots: 10, maxChars: 90000 };

export const journeyLabel = (list, key) => list.find(x => x.key === key)?.label || key || '';
